/**
 * Theme Context
 *
 * Handles dark/light theme switching with:
 * - Auto-detection of system preference (prefers-color-scheme)
 * - Manual override via toggle button
 * - Persistence via localStorage
 * - Listening for system theme changes in real-time
 */
import React, { createContext, useState, useEffect, useCallback } from "react";

// Storage key for persisted theme preference
const THEME_STORAGE_KEY = "wotodo-theme-preference";

// Possible values: 'light', 'dark', 'system'
const THEME_VALUES = {
  LIGHT: "light",
  DARK: "dark",
  SYSTEM: "system",
};

/**
 * Get the system (OS/browser) preferred color scheme
 * @returns {'light'|'dark'}
 */
function getSystemTheme() {
  if (
    window.matchMedia &&
    window.matchMedia("(prefers-color-scheme: dark)").matches
  ) {
    return THEME_VALUES.DARK;
  }
  return THEME_VALUES.LIGHT;
}

/**
 * Resolve the effective theme from the user's preference
 * @param {'light'|'dark'|'system'} preference
 * @returns {'light'|'dark'}
 */
function resolveTheme(preference) {
  if (preference === THEME_VALUES.SYSTEM) {
    return getSystemTheme();
  }
  return preference;
}

/**
 * Apply the theme to the document element
 * @param {'light'|'dark'} theme
 */
function applyTheme(theme) {
  document.documentElement.setAttribute("data-theme", theme);

  // Update the meta theme-color for mobile browsers
  const metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (metaThemeColor) {
    metaThemeColor.setAttribute(
      "content",
      theme === THEME_VALUES.DARK ? "#191919" : "#ffffff"
    );
  }
}

// Create the context
export const ThemeContext = createContext({
  theme: THEME_VALUES.LIGHT, // effective theme ('light' or 'dark')
  preference: THEME_VALUES.SYSTEM, // user preference ('light', 'dark', or 'system')
  setThemePreference: () => {},
  cycleTheme: () => {},
});

/**
 * Theme Provider component
 * Wraps the app and provides theme state + controls
 */
export default function ThemeProvider({ children }) {
  // Load saved preference (default to 'system')
  const [preference, setPreference] = useState(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    if (
      saved === THEME_VALUES.LIGHT ||
      saved === THEME_VALUES.DARK ||
      saved === THEME_VALUES.SYSTEM
    ) {
      return saved;
    }
    return THEME_VALUES.SYSTEM;
  });

  // Resolved effective theme
  const [theme, setTheme] = useState(() => resolveTheme(preference));

  /**
   * Set theme preference and persist it
   */
  const setThemePreference = useCallback((newPreference) => {
    setPreference(newPreference);
    localStorage.setItem(THEME_STORAGE_KEY, newPreference);
  }, []);

  /**
   * Cycle through themes: system → light → dark → system
   */
  const cycleTheme = useCallback(() => {
    setPreference((current) => {
      let next;
      if (current === THEME_VALUES.SYSTEM) {
        next = THEME_VALUES.LIGHT;
      } else if (current === THEME_VALUES.LIGHT) {
        next = THEME_VALUES.DARK;
      } else {
        next = THEME_VALUES.SYSTEM;
      }
      localStorage.setItem(THEME_STORAGE_KEY, next);
      return next;
    });
  }, []);

  /**
   * Whenever preference changes, resolve and apply the effective theme
   */
  useEffect(() => {
    const resolved = resolveTheme(preference);
    setTheme(resolved);
    applyTheme(resolved);
  }, [preference]);

  /**
   * Listen for system theme changes (when preference is 'system')
   */
  useEffect(() => {
    if (preference !== THEME_VALUES.SYSTEM) return;

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleChange = (event) => {
      const newTheme = event.matches ? THEME_VALUES.DARK : THEME_VALUES.LIGHT;
      setTheme(newTheme);
      applyTheme(newTheme);
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
    }

    return () => {
      if (mediaQuery.removeEventListener) {
        mediaQuery.removeEventListener("change", handleChange);
      } else {
        mediaQuery.removeListener(handleChange);
      }
    };
  }, [preference]);

  /**
   * Apply theme on initial mount
   */
  useEffect(() => {
    applyTheme(resolveTheme(preference));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ThemeContext.Provider
      value={{
        theme,
        preference,
        setThemePreference,
        cycleTheme,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
}
